import { useState, useMemo, useEffect, useRef, useContext } from "react";
import { AgGridReact } from "ag-grid-react"; // the AG Grid React Component
import { Button, message } from "antd";

import { ColDef } from "ag-grid-enterprise";
import { useSubscription } from "@apollo/client";
import { SUBSCRIPTION_LEVEL_COMPANY_LIST } from "services/graphQL/subscriptions";
import "./companies.scss";
import {
  RoleSubscriptionEnum,
  SubscriptionContext
} from "context/SubscriptionProvider";
import { GridLoadingIndicator } from "components/widgets";
import {
  FilterChipComponent,
  FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import CiqLogPageHeader, {
  calculateLogPageStats
} from "components/ciq-log-page-header";
import CiqAgSearch from "components/ciq-ag-search";
import { getSubscriptionVendorSources } from "utils/utils";
import AddUpdateCompanyInfoModal from "components/add-company/add-company";
import {
  dummyColumnDef,
  truncatedTextCellRenderer,
  TruncatedTextWithTooltip
} from "components/cell-renders";
import { getAccessToken, getProjectListV2 } from "services/auth";
import { ErrorMessages, ESubmittalMgmtModes } from "../../constants";

export enum DrawerTitle {
  ADD_COMPANY = "Add Company",
  COMPANY_DETAILS = "View Company Details",
  UPDATE_COMPANY = "Update Company Details"
}

function CompanyList() {
  const subscriptionToken: any = getAccessToken();
  const gridRef = useRef<AgGridReact>(null);
  const [isGridReady, setGridReady] = useState(false);
  const [drawer, setDrawerVisibility] = useState<boolean>(false);
  const [editCompanyData, setEditCompanyData] = useState<any>();
  const { subscriptionRole } = useContext(SubscriptionContext);
  const [projectListData, setProjectListData] = useState<any>([]);

  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();
  const filterChipRef = useRef<FilterChipComponentRef>(null);

  const {
    data: companyData,
    error: companyDataError,
    loading
  } = useSubscription(SUBSCRIPTION_LEVEL_COMPANY_LIST, {
    shouldResubscribe: true
  });

  const [allCompanies, setCompanyList] = useState([]);

  useEffect(() => {
    const fetchProjectListData = async () => {
      try {
        const res = await getProjectListV2(subscriptionToken);

        if (res && res.success && Array.isArray(res.data?.data)) {
          setProjectListData([...res.data.data]);
        } else {
          message.error("An error occurred while fetching projects.");
          if (res && res.error) {
            console.error("Project list fetch error:", res.error);
          }
        }
      } catch (error) {
        message.error("An unexpected error occurred.");
        console.error("Project list fetch exception:", error);
      }
    };

    fetchProjectListData();
    return () => {};
  }, [subscriptionToken]);

  const isIntegratedProjectExist = useMemo(() => {
    if (projectListData && projectListData?.length > 0) {
      const isIntegratedProjExist = projectListData?.some(
        (proj: any) => proj?.mode === ESubmittalMgmtModes.INTEGRATION_MODE
      );
      return isIntegratedProjExist;
    }
    return false;
  }, [projectListData]);

  function CompanyNameComparator(
    currentStringValue: string,
    nextStringValue: string
  ) {
    return currentStringValue
      .toLowerCase()
      .localeCompare(nextStringValue.toLowerCase());
  }

  useEffect(() => {
    if (isGridReady && gridRef && gridRef.current) {
      gridRef.current?.api?.setGridOption("loading", loading);
    }
    if (companyDataError) {
      gridRef.current?.api.hideOverlay();
      message.error(companyDataError.message);
      return;
    }
    if (!companyData) return;
    setCompanyList(companyData.subscription_vendors);
  }, [companyDataError, isGridReady, loading, companyData]);

  function companyValueSetter(params: any) {
    // eslint-disable-next-line no-param-reassign
    params.data.make = params.newValue;
    return true;
  }

  function pointOfContactsSetter(params: any) {
    const pocs: any = params?.data?.trade_partner_pocs;
    if (pocs?.length > 0) {
      const pocstr = `${pocs[0].first_name} ${pocs[0].last_name}`;

      if (pocs?.length === 1) {
        return pocstr;
      }
      return `${pocstr} +${pocs.length - 1}`;
    }

    return "";
  }

  function pointOfContactsSetter1(params: any) {
    const pocs: any = params?.data?.trade_partner_pocs;
    if (pocs?.length > 0) {
      const pocstr = `${pocs[0].first_name} ${pocs[0].last_name}`;

      if (pocs?.length === 1) {
        return <TruncatedTextWithTooltip text={pocstr} />;
      }
      return (
        <div className="flex items-center gap-x-1">
          <TruncatedTextWithTooltip text={pocstr} />
          <div className="text-blue-500"> +{pocs.length - 1}</div>
        </div>
      );
    }

    return "";
  }

  const columnDefs: ColDef[] = useMemo<ColDef[]>(() => {
    const arr: ColDef[] = [
      dummyColumnDef,
      {
        headerName: "Serial No.",
        valueGetter: "node.rowIndex + 1",
        headerTooltip: "Serial No.",
        menuTabs: [],
        width: 100,
        sortable: false,
        editable: false,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "name",
        field: "name",
        headerName: "Company Name",
        headerTooltip: "Company Name",
        menuTabs: [],
        filter: false,
        valueSetter: companyValueSetter,
        comparator: CompanyNameComparator,
        sort: "asc",
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "address",
        field: "address",
        headerName: "Address",
        headerTooltip: "Address",
        menuTabs: [],
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "point_of_contact",
        headerName: "Points of Contact",
        headerTooltip: "Points of Contact",
        menuTabs: [],
        resizable: true,
        sortable: true,
        field: "point_of_contact",
        cellRenderer: pointOfContactsSetter1,
        comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any) => {
          const pocA = pointOfContactsSetter(nodeA);
          const pocB = pointOfContactsSetter(nodeB);
          return pocA.toLowerCase().localeCompare(pocB.toLowerCase());
        }
      },
      {
        colId: "external_source_id",
        headerName: "Source",
        menuTabs: ["filterMenuTab"],
        hide: !isIntegratedProjectExist,
        valueGetter: ({ data }) => {
          const allSources = getSubscriptionVendorSources(data);
          return allSources.join(",");
        },
        cellRenderer: truncatedTextCellRenderer
      }
    ];

    return arr;
  }, [isIntegratedProjectExist]);

  const defaultColDef: {} = useMemo<ColDef>(() => {
    return {
      sortable: true,
      autoHeight: true,
      resizable: true
    };
  }, []);

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef && gridRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);
  });

  const onFiltersApplied = () => {
    filterChipRef.current?.onFilterApplied();
    setStatsData(
      calculateLogPageStats({
        gridRef
      })
    );
  };

  const headerItems = [
    <CiqAgSearch
      key="SubscriptionCompany_search"
      gridRef={gridRef}
      placeholder="Search Companies"
    />,
    <FilterChipComponent
      key="SubscriptionCompany_FilterChip"
      gridRef={gridRef}
      ref={filterChipRef}
    />,
    <Button
      type="primary"
      key="SubscriptionCompany_AddCompany"
      onClick={() => {
        setEditCompanyData(undefined);
        setDrawerVisibility(true);
      }}
      title={
        subscriptionRole <= RoleSubscriptionEnum.subscription_viewer
          ? ErrorMessages.PermissionNotGranted
          : DrawerTitle.ADD_COMPANY
      }
      disabled={subscriptionRole <= RoleSubscriptionEnum.subscription_viewer}
      data-testid="add-company-button"
    >
      {DrawerTitle.ADD_COMPANY}
    </Button>
  ];

  return (
    <div className="flex flex-col h-screen">
      <CiqLogPageHeader
        {...{
          entityName: "Companies",
          titleParam: {
            title: "Companies",
            totalCount: allCompanies?.length,
            filterStats: statsData
          },
          gridRef,
          items: headerItems
        }}
      />
      <div className="grow ag-theme-alpine">
        <AgGridReact<any>
          ref={gridRef}
          onGridReady={() => {
            setGridReady(true);
            gridRef?.current?.api?.sizeColumnsToFit();
          }}
          rowSelection={{ mode: "singleRow", checkboxes: false }}
          rowData={allCompanies}
          columnDefs={columnDefs} // Column Defs for Columns
          defaultColDef={defaultColDef} // Default Column Properties
          animateRows={false} // Optional - set to 'true' to have rows animate when sorted
          loadingOverlayComponent={GridLoadingIndicator}
          suppressDragLeaveHidesColumns
          onSortChanged={(e) => {
            e.api.refreshCells();
          }}
          onFilterChanged={(e) => {
            e.api.refreshCells();
            onFiltersApplied();
          }}
          onCellClicked={(e) => {
            setEditCompanyData(e.data);
            setDrawerVisibility(true);
          }}
          tooltipShowDelay={0}
          tooltipHideDelay={2000}
          tooltipShowMode="whenTruncated"
        />
      </div>
      {drawer && (
        <AddUpdateCompanyInfoModal
          setDrawerOpen={setDrawerVisibility}
          showDrawerOpen={drawer}
          companyInfo={editCompanyData}
          setCompanyInfo={setEditCompanyData}
        />
      )}
    </div>
  );
}

export default CompanyList;
